import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Loader2, Sparkles, Check, X } from "lucide-react";
import { api, type VoiceLessonView } from "../../lib/api.js";
import { useDismiss } from "../../components/ui/useDismiss.js";

/**
 * The learning loop, where the operator can see it and decide.
 *
 * Every copy change is kept as a before/after pair — in the post editor, in the
 * pipeline's Social tab, in Postiz itself, or a re-write with "what didn't
 * work". "Learn from edits" reads the pending ones with one model call and
 * PROPOSES short house rules. None reaches the writer until it is approved here:
 * a one-off correction must not quietly become policy. Approved rules, plus a
 * few real before/after examples, go into every caption written from then on.
 */

const SOURCE_HE = { edit: "עריכה ידנית", postiz: "נערך בפוסטיז", steer: "כתיבה מחדש עם הנחיה" } as const;

export function VoiceLessonsDialog({ onClose }: { onClose: () => void }) {
  const dismiss = useDismiss(onClose);
  const voice = useQuery({ queryKey: ["social-voice"], queryFn: () => api.socialVoice() });
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const distill = useMutation({
    mutationFn: () => { setError(null); setNote(null); return api.distillSocialVoice(); },
    onSuccess: (r) => {
      setNote(
        r.proposed.length > 0
          ? `נקראו ${r.editsRead} עריכות · הוצעו ${r.proposed.length} כללים לאישור · $${r.costUsd.toFixed(3)}`
          : `נקראו ${r.editsRead} עריכות · לא עלה כלל כללי חדש · $${r.costUsd.toFixed(3)}`,
      );
      void voice.refetch();
    },
    onError: (e) => setError((e as Error).message),
  });

  const decide = useMutation({
    mutationFn: (o: { id: number; status?: VoiceLessonView["status"]; rule?: string }) =>
      api.decideVoiceLesson(o.id, { ...(o.status ? { status: o.status } : {}), ...(o.rule ? { rule: o.rule } : {}) }),
    onSuccess: () => void voice.refetch(),
    onError: (e) => setError((e as Error).message),
  });

  const lessons = voice.data?.lessons ?? [];
  const pending = voice.data?.pendingEdits ?? 0;
  const proposed = lessons.filter((l) => l.status === "proposed");
  const approved = lessons.filter((l) => l.status === "approved");
  const rejected = lessons.filter((l) => l.status === "rejected");

  return (
    <div {...dismiss} className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" dir="rtl">
      <div className="flex max-h-[92vh] w-full max-w-xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <header className="flex shrink-0 items-center justify-between border-b border-line px-4 py-3">
          <h2 className="text-sm font-semibold">למידה מעריכות</h2>
          <button onClick={onClose} className="text-sm text-muted hover:text-ink">סגירה</button>
        </header>

        <div className="min-h-0 grow space-y-4 overflow-y-auto p-4 text-sm">
          <p className="text-xs text-muted">
            כל שינוי שעושים בכיתוב נשמר: הגרסה שהמערכת כתבה לצד הגרסה שאישרתם. "הפקת כללים" קוראת את העריכות
            החדשות ומציעה כללי כתיבה קצרים. כלל נכנס לכתיבה רק אחרי שאישרתם אותו, ואז הוא חל על כל פוסט שנכתב
            מכאן והלאה, יחד עם כמה דוגמאות אמיתיות של לפני ואחרי.
          </p>

          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-line p-2.5">
            <span className="text-xs">{voice.isLoading ? "טוען…" : `${pending} עריכות חדשות שעוד לא נלמדו`}</span>
            <span className="grow" />
            <button
              onClick={() => distill.mutate()}
              disabled={pending === 0 || distill.isPending || voice.data?.distillRunning}
              className="flex items-center gap-1.5 rounded-lg border border-brand-700 px-2.5 py-1.5 text-xs font-medium text-brand-800 disabled:opacity-40"
            >
              {distill.isPending ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
              {distill.isPending ? "קורא עריכות…" : "הפקת כללים מהעריכות"}
            </button>
            <span className="w-full text-[10px] text-muted">בערך $0.01–0.03 להרצה · עד 60 עריכות · כ־20 שניות</span>
          </div>

          {note && <p className="text-xs text-emerald-700">{note}</p>}
          {error && <p className="rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-900">{error}</p>}

          <Section title={`מחכים לאישור (${proposed.length})`} empty="אין כללים שמחכים להחלטה.">
            {proposed.map((l) => (
              <LessonRow key={l.id} lesson={l} busy={decide.isPending}
                onApprove={(rule) => decide.mutate({ id: l.id, status: "approved", ...(rule !== l.rule ? { rule } : {}) })}
                onReject={() => decide.mutate({ id: l.id, status: "rejected" })} />
            ))}
          </Section>

          <Section title={`פעילים בכתיבה (${approved.length})`} empty="עוד אין כללים פעילים.">
            {approved.map((l) => (
              <LessonRow key={l.id} lesson={l} busy={decide.isPending}
                onApprove={(rule) => { if (rule !== l.rule) decide.mutate({ id: l.id, rule }); }}
                approveLabel="שמירת ניסוח"
                onReject={() => decide.mutate({ id: l.id, status: "rejected" })} rejectLabel="הפסקה" />
            ))}
          </Section>

          {rejected.length > 0 && (
            <details className="rounded-lg border border-line px-3 py-2">
              <summary className="cursor-pointer text-xs text-muted">נדחו ({rejected.length}) · לא יוצעו שוב</summary>
              <ul className="mt-2 space-y-1">
                {rejected.map((l) => (
                  <li key={l.id} className="flex items-center gap-2 text-xs text-muted">
                    <span className="grow">{l.rule}</span>
                    <button onClick={() => decide.mutate({ id: l.id, status: "approved" })} className="underline">הפעלה</button>
                  </li>
                ))}
              </ul>
            </details>
          )}

          {(voice.data?.recentEdits.length ?? 0) > 0 && (
            <details className="rounded-lg border border-line px-3 py-2">
              <summary className="cursor-pointer text-xs text-muted">העריכות האחרונות שנשמרו</summary>
              <ul className="mt-2 space-y-3">
                {voice.data!.recentEdits.map((e) => (
                  <li key={e.id} className="text-xs">
                    <p className="text-[11px] text-muted">
                      {e.platform} · {e.format} · {SOURCE_HE[e.source]}{e.distilledAt ? " · נלמד" : ""}
                    </p>
                    {e.steer && <p className="text-[11px]">הנחיה: {e.steer}</p>}
                    <p className="mt-1 whitespace-pre-wrap rounded bg-rose-50 px-2 py-1 line-through decoration-rose-300">{e.beforeBody}</p>
                    <p className="mt-1 whitespace-pre-wrap rounded bg-emerald-50 px-2 py-1">{e.afterBody}</p>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, empty, children }: { title: string; empty: string; children: React.ReactNode[] }) {
  return (
    <section>
      <h3 className="mb-1.5 text-xs font-semibold">{title}</h3>
      {children.length === 0 ? <p className="text-xs text-muted">{empty}</p> : <ul className="space-y-2">{children}</ul>}
    </section>
  );
}

function LessonRow({
  lesson, busy, onApprove, onReject, approveLabel = "אישור", rejectLabel = "דחייה",
}: {
  lesson: VoiceLessonView;
  busy: boolean;
  onApprove: (rule: string) => void;
  onReject: () => void;
  approveLabel?: string;
  rejectLabel?: string;
}) {
  const [rule, setRule] = useState(lesson.rule);
  return (
    <li className="rounded-lg border border-line p-2.5">
      <textarea
        value={rule} dir="rtl" rows={2}
        onChange={(e) => setRule(e.target.value)}
        className="w-full rounded border border-line bg-canvas px-2 py-1 text-sm"
      />
      {lesson.why && <p className="mt-1 text-[11px] text-muted">{lesson.why}</p>}
      <div className="mt-2 flex items-center gap-2">
        <button
          onClick={() => onApprove(rule.trim())}
          disabled={busy || rule.trim().length < 4}
          className="flex items-center gap-1 rounded-lg border border-emerald-600 px-2 py-1 text-xs text-emerald-800 disabled:opacity-40"
        >
          <Check size={12} /> {approveLabel}
        </button>
        <button
          onClick={onReject}
          disabled={busy}
          className="flex items-center gap-1 rounded-lg border border-line px-2 py-1 text-xs text-muted disabled:opacity-40"
        >
          <X size={12} /> {rejectLabel}
        </button>
        {lesson.evidenceEditIds.length > 0 && (
          <span className="text-[10px] text-muted">מבוסס על {lesson.evidenceEditIds.length} עריכות</span>
        )}
      </div>
    </li>
  );
}
